import { NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    Input,
    Output,
    ViewChild,
    effect,
    inject,
    input,
    signal,
    type AfterViewChecked,
} from '@angular/core';
import { UiButtonComponent } from '@big-direkt/ui/button';
import { WindowService } from '@big-direkt/utils/environment';
import { IconBigMediumWinkelUnten, IconComponent } from '@big-direkt/utils/icons';
import { PlatformService } from '@big-direkt/utils/shared';
import { TranslocoDirective } from '@jsverse/transloco';
import { IsActivePipe } from './is-active.pipe';
import { type ResultFilterModel } from './result-filter.model';

@Component({
    selector: 'big-ui-result-filter',
    templateUrl: './result-filter.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [IsActivePipe, NgClass, TranslocoDirective, UiButtonComponent, IconComponent],
})
export class ResultFilterComponent implements AfterViewChecked {
    private readonly platformService = inject(PlatformService);
    private readonly windowService = inject(WindowService);

    public readonly iconAngleDown = IconBigMediumWinkelUnten;
    @Output() public readonly activeFilter = new EventEmitter<ResultFilterModel[]>();

    @Input() public title?: string;
    @Input() public allowMultiple = true;

    @ViewChild('buttonWrapper') private readonly buttonWrapper?: ElementRef<Element>;
    @HostBinding('class') public classList = 'block';

    public hasOverflow = signal<boolean>(false);
    public showAllFilters = signal<boolean>(false);
    public filteredItems = signal<ResultFilterModel[]>([]);
    public canToggleFiltersList = input(true);

    @Input() public set filter(value: ResultFilterModel[] | null) {
        const safeguardedValue = value ?? [];
        this.filteredItems.set(safeguardedValue);

        if (this.getActiveFilters().length) {
            return;
        }

        this.showAllFilters.set(false);
        this.hasOverflow.set(false);
    }

    public constructor() {
        effect(
            () => {
                this.showAllFilters.set(!this.canToggleFiltersList());
            },
        );
    }

    public ngAfterViewChecked(): void {
        if (this.showAllFilters() || this.platformService.isPlatformServer()) {
            return;
        }

        /**
         * We need the next frame to get the scroll height after the dom has been rendered
         */
        // wee need this condition to prevent errors on ssr
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        this.windowService.nativeWindow()?.requestAnimationFrame((): void => {
            if (!this.buttonWrapper?.nativeElement) {
                return;
            }

            this.hasOverflow.set(this.buttonWrapper.nativeElement.scrollHeight > this.buttonWrapper.nativeElement.clientHeight);
        });
    }

    public toggleFilter(item: ResultFilterModel): void {
        const newIsActive = !item.isActive;

        if (newIsActive && !this.allowMultiple) {
            this.getActiveFilters().forEach(activeItem => (activeItem.isActive = false));
        }

        item.isActive = newIsActive;

        this.activeFilter.emit(this.getActiveFilters());
    }

    public clearFilters(): void {
        this.filteredItems().forEach(item => (item.isActive = false));

        this.activeFilter.emit(this.getActiveFilters());
    }

    public toggleFiltersList(): void {
        this.showAllFilters.set(!this.showAllFilters());
    }

    private getActiveFilters(): ResultFilterModel[] {
        return this.filteredItems().filter(item => item.isActive);
    }
}
